import { useCallback, useEffect, useState } from "react"
import { Link, useNavigate } from "react-router-dom"
import { ArrowLeft, Info, RefreshCw } from "lucide-react"
import {
  getActivityLog,
  getBookingSummary,
  type ActivityLogEntry,
  type BookingSummary,
} from "@/lib/api"
import { cn } from "@/lib/utils"
import {
  ChartCard,
  EventBadge,
  InfoHint,
  SectionHeading,
} from "@/components/dashboard-cards"
import { PlaceholderCard } from "@/components/PlaceholderCard"

/**
 * The back room. Everything here answers "is the machine running?" or "what
 * would we build next?" — neither of which is a question the Dashboard is for,
 * and both of which were stealing the bottom third of its scroll.
 *
 * No cohort tabs on purpose: an ops signal is about the SYSTEM, not a slice of
 * people, so these read every cohort over the full window and say so.
 */

/** The endpoints' own cap on `days` — same 365 the dashboard calls "all time". */
const ALL_TIME_DAYS = 365

export function OpsPage() {
  const navigate = useNavigate()
  const [activity, setActivity] = useState<ActivityLogEntry[]>([])
  const [bookingSummary, setBookingSummary] = useState<BookingSummary | null>(null)
  const [isLoading, setIsLoading] = useState(true)

  const load = useCallback(async () => {
    setIsLoading(true)
    // Each tile fails on its own — one dead endpoint shouldn't blank the page.
    const [log, bookings] = await Promise.all([
      getActivityLog(200).catch(() => [] as ActivityLogEntry[]),
      getBookingSummary(ALL_TIME_DAYS).catch(() => null),
    ])
    setActivity(log)
    setBookingSummary(bookings)
    setIsLoading(false)
  }, [])

  useEffect(() => {
    load()
  }, [load])

  const recent = [...activity]
    .sort((a, b) => Date.parse(b.created_at) - Date.parse(a.created_at))
    .slice(0, 12)

  return (
    <div className="p-6 space-y-8">
      <div>
        <div className="flex items-start justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold">Ops</h1>
            <Link
              to="/"
              className="mt-1 inline-flex items-center gap-1 text-base text-muted-foreground hover:text-foreground"
            >
              <ArrowLeft className="size-3.5" />
              Performance Dashboard
            </Link>
          </div>
          <button
            onClick={load}
            disabled={isLoading}
            className="inline-flex h-8 shrink-0 items-center gap-1.5 rounded-md border bg-background px-2.5 text-sm hover:bg-muted disabled:opacity-50"
            title="Re-read the cron run, activity log and booking summary"
          >
            <RefreshCw className={cn("size-3.5", isLoading && "animate-spin")} />
            Refresh
          </button>
        </div>
        <p className="text-base text-muted-foreground mt-3 max-w-3xl">
          Everyone, all time{" "}
          <InfoHint label="Why there are no cohort tabs">
            The cohort tabs on the Dashboard don't apply here — these tiles always
            read across every user, for all time.
          </InfoHint>{" "}
          · some tiles read{" "}
          <span className="inline-flex items-center gap-1 rounded-full border border-dashed px-1.5 py-0.5 text-sm uppercase tracking-wide text-muted-foreground align-middle">
            Not yet tracked
          </span>{" "}
          <InfoHint label="What that badge means">
            That tile is missing instrumentation, not missing usage — we'd rather
            say so than show a placeholder number.
          </InfoHint>
        </p>
      </div>

      <section>
        <SectionHeading
          title="Operations & Deep Dive"
          description="Live operational signals and drill-in tables from the activity log."
        />
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">

          <ChartCard title="Recent Activity" subtitle="Latest generation events">
            {recent.length === 0 ? (
              <p className="text-base text-muted-foreground py-8 text-center">
                {isLoading ? "Loading…" : "No recent activity"}
              </p>
            ) : (
              <div className="space-y-1.5 max-h-[220px] overflow-y-auto">
                {recent.map((e) => (
                  <button
                    key={e.id}
                    onClick={() => e.user_id && navigate(`/users/${e.user_id}`)}
                    className="w-full flex items-center justify-between gap-2 text-left text-sm rounded px-1.5 py-1 hover:bg-accent/50"
                  >
                    <span className="truncate font-medium">{e.user_name || "Unknown"}</span>
                    <span className="flex items-center gap-2 shrink-0">
                      <EventBadge type={e.event_type} />
                      <span className="text-muted-foreground tabular-nums">
                        {new Date(e.created_at).toLocaleDateString("en-US", { month: "short", day: "numeric" })}
                      </span>
                    </span>
                  </button>
                ))}
              </div>
            )}
          </ChartCard>

          <PlaceholderCard
            title="Most Active Users"
            need="Needs per-user activity we don't collect yet — completed workouts, logins, chats, edits. Ranking on generation events would just rank tenure."
          />
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mt-4">
          {bookingSummary && bookingSummary.by_studio.length > 0 ? (
            <ChartCard title="Studio Leaderboard" subtitle="Top studios by bookings · all time">
              <div className="space-y-1.5 py-1">
                {bookingSummary.by_studio.slice(0, 10).map((s, i) => (
                  <div key={s.studio_id} className="flex items-center justify-between text-base">
                    <span className="truncate">
                      <span className="text-muted-foreground mr-2">{i + 1}.</span>
                      {s.studio_name}
                    </span>
                    <span className="text-muted-foreground tabular-nums">
                      {s.count} · {s.confirmed} confirmed · {s.unique_users} members
                    </span>
                  </div>
                ))}
              </div>
            </ChartCard>
          ) : (
            <PlaceholderCard
              title="Studio Leaderboard"
              need={bookingSummary ? "No bookings in the window to rank." : "Couldn't load booking summary this load."}
            />
          )}
          <PlaceholderCard
            title="Top Chat Prompts"
            need="Needs a classification pass over chat history to rank prompts by topic."
          />
        </div>
      </section>

      <section>
        <SectionHeading
          title="Not Yet Tracked"
          description="Metrics the dashboard is designed around but the backend can't answer yet. They live here rather than in the main scroll so a real number is never sitting next to an empty frame."
        />
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <PlaceholderCard
            title="Cohort Retention Curves (D1 / D7 / D30)"
            need="Needs a per-day activity signal per user. We have signup dates, not 'active on day N'."
          />
          <PlaceholderCard
            title="Feature Adoption Matrix"
            need="Needs per-user, per-week usage events for workouts, bookings, chat and edits — most aren't logged yet."
          />
        </div>
      </section>

      <p className="flex items-center gap-1.5 text-base text-muted-foreground">
        <Info className="size-3.5" />
        Per-user history is on the{" "}
        <Link to="/users" className="font-medium text-foreground hover:text-primary">
          Users
        </Link>{" "}
        tab.
      </p>
    </div>
  )
}
